import { useState, useEffect, Fragment, useRef, useCallback } from "react";
import "./styles.sass";
import assets from "../../assets";
import useHandler from "./handler";
import { ToggleButton } from "../../role-based-code/toggle-button";
import { MockTestHero } from "../../components/mock-test-hero";
import { LatexRenderer } from "../../components/latex-renderer";

// ── Sub-Components (Defined at top-level to prevent re-mounting) ──

const QuizDropdown = ({
  label,
  refKey,
  dropKey,
  value,
  options,
  onSelect,
  placeholder,
  prerequisite,
  prerequisiteLabel,
  setOpenDropdown,
  openDropdown,
  isLoading,
}) => (
  <section
    className="subject_wrapper"
    ref={refKey}
    onClick={() => setOpenDropdown(dropKey)}
  >
    <h4>{label}</h4>
    <section className="drop_down_wrapper">
      <p>{value || placeholder}</p>
      <img src={assets.ic_chevron_down} alt="chevron" />
    </section>
    {openDropdown === dropKey && (
      <section className="options_list">
        {prerequisite !== undefined && !prerequisite ? (
          <p
            style={{
              textAlign: "center",
              marginTop: "35%",
              color: "#22c55e",
            }}
          >
            Please select {prerequisiteLabel} first
          </p>
        ) : isLoading ? (
          <p
            style={{
              textAlign: "center",
              marginTop: "35%",
              color: "#22c55e",
            }}
          >
            Loading {label.toLowerCase()}...
          </p>
        ) : options?.length > 0 ? (
          options.map((item, i) => (
            <p
              key={i}
              className="option_item"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item);
                setOpenDropdown(null);
              }}
            >
              {item}
            </p>
          ))
        ) : (
          <p
            style={{
              textAlign: "center",
              marginTop: "35%",
              color: "#ef4444",
            }}
          >
            No {label.toLowerCase()} found
          </p>
        )}
      </section>
    )}
  </section>
);

const QuizSetupModal = ({
  classdetails,
  selectedYear,
  getQuizDuration,
  setShowSetupPopup,
  setHidePanals,
  setIsQuizActive,
  confirmStartQuiz,
  mode,
}) => (
  <div className="quiz_setup_overlay">
    <div className="quiz_setup_modal">
      <div className="modal_header">
        <h2>Quiz Setup</h2>
        <p>Review your quiz configuration</p>
      </div>

      <div className="modal_body">
        <div className="config_item">
          <div className="icon_box subject_bg">
            <img src={assets.ic_book_voilet} alt="Subject" />
          </div>
          <div className="config_info">
            <span>Exam</span>
            <h4>{classdetails?.data?.class || "NEET/JEE"}</h4>
          </div>
        </div>

        <div className="config_item">
          <div className="icon_box paper_bg">
            <img src={assets.ic_active_files} alt="Year" />
          </div>
          <div className="config_info">
            <span>Selected Year</span>
            <h4>{selectedYear || "Not Selected"}</h4>
          </div>
        </div>

        <div className="stats_row">
          <div className="stat_card duration_card">
            <div className="stat_icon_box">
              <img src={assets.ic_timer} alt="Duration" />
            </div>
            <div className="stat_info">
              <span>DURATION</span>
              <h4>{Math.floor(getQuizDuration() / 60)} min</h4>
            </div>
          </div>
        </div>

        <div className="warning_box">
          <div className="warning_icon">
            <img src={assets.ic_info} alt="Warning" />
          </div>
          <p>
            Once you start the quiz, the timer will begin automatically. Make
            sure you're ready! Before final submission, you can review your
            answers and complete any unanswered questions.
          </p>
        </div>

        {mode && (
          <div className="warning_box exam_mode_note">
            <div className="warning_icon">
              <img src={assets.ic_info} alt="Warning" />
            </div>
            <p>
              <strong>Exam Mode Active:</strong> The test will open in{" "}
              <strong>Fullscreen Mode</strong> to provide an immersive
              experience. You can exit fullscreen anytime by pressing the{" "}
              <strong>Esc</strong> key. Focus entirely on the assessment!
            </p>
          </div>
        )}
      </div>

      <div className="modal_footer">
        <button className="cancel_btn" onClick={() => setShowSetupPopup(false)}>
          Cancel
        </button>
        <button
          className="start_btn"
          onClick={() => {
            setHidePanals(true);
            setIsQuizActive(true);
            confirmStartQuiz();
          }}
        >
          Start Quiz
        </button>
      </div>
    </div>
  </div>
);

const QuestionPaletteScreen = ({
  questions,
  selectedAnswers,
  setCurrentQIndex,
  setIsReviewMode,
  setIsQuizActive,
  setShowReviewPopup,
  onSubmitQuiz,
  submitLoading,
}) => {
  const unansweredCount = questions.filter(
    (_, i) => selectedAnswers[i] === undefined,
  ).length;
  return (
    <section className="review_palette_screen_container">
      <div className="review_palette_card">
        <div className="review_palette_header">
          <h2>Question Palette</h2>
          <p>
            You have <strong>{unansweredCount}</strong> unanswered question
            {unansweredCount !== 1 ? "s" : ""}. Click on any question number to
            review.
          </p>
        </div>
        <div className="review_palette_section">
          <div className="review_palette_title_row">
            <span>Question Palette</span>
            <span className="review_q_badge">{questions.length} Questions</span>
          </div>
          <div className="review_palette_grid">
            {questions.map((_, index) => {
              const isAns = selectedAnswers[index] !== undefined;
              return (
                <div
                  key={index}
                  className={`review_circle ${isAns ? "review_answered" : "review_unanswered"}`}
                  onClick={() => {
                    setCurrentQIndex(index);
                    setIsReviewMode(true);
                    setIsQuizActive(true);
                    setShowReviewPopup(false);
                  }}
                >
                  {String(index + 1).padStart(2, "0")}
                </div>
              );
            })}
          </div>
          <div className="review_legend">
            <div className="legend_item">
              <span className="legend_dot answered_dot"></span>
              <span>Answered</span>
            </div>
            <div className="legend_item">
              <span className="legend_dot unanswered_dot"></span>
              <span>Unanswered</span>
            </div>
          </div>
        </div>
        <div className="review_palette_footer">
          <button
            className="review_cancel_btn"
            onClick={() => {
              setIsQuizActive(true);
              setShowReviewPopup(false);
            }}
          >
            Cancel
          </button>
          <button
            className="review_submit_btn"
            onClick={() => {
              setIsQuizActive(false);
              setShowReviewPopup(false);
              onSubmitQuiz();
            }}
          >
            {submitLoading && (
              <section className="submit_test_spinner"></section>
            )}
            Submit Quiz
          </button>
        </div>
      </div>
    </section>
  );
};

const QuestionCardInner = ({
  qIndex,
  questions,
  selectedAnswers,
  mode,
  questionDetails,
  handleOptionSelect,
  isReviewMode,
  setCurrentQIndex,
  setShowReviewPopup,
  setIsQuizActive,
}) => {
  const q = questions[qIndex];
  const scrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkScroll = () => {
      const isScrollable = el.scrollHeight > el.clientHeight + 10;
      const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 15;
      setShowScrollHint(isScrollable && !isAtBottom);
    };

    checkScroll();
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    el.addEventListener("scroll", checkScroll);
    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", checkScroll);
    };
  }, [q?.question, q?.options]);

  if (!q) return null;
  const selOpt = selectedAnswers[qIndex];
  const isAnsweredQ = selOpt !== undefined;
  const isCorrectQ = isAnsweredQ && q?.options[selOpt] === q?.answer;

  return (
    <Fragment>
      <section className="question-section" ref={scrollRef}>
        <h3 className="question-title"><LatexRenderer text={q?.question} /></h3>

        {isAnsweredQ && !mode && (
          <div
            className={`answer-feedback ${isCorrectQ ? "correct" : "wrong"}`}
          >
            {isCorrectQ ? (
              <>
                <div className="feedback_icon correct">✓</div>
                <span>Correct Answer!</span>
              </>
            ) : (
              <>
                <div className="feedback_icon wrong">✕</div>
                <span>Incorrect Answer!</span>
              </>
            )}
          </div>
        )}

        {isAnsweredQ && !mode && !isCorrectQ && (
          <section className="explaination_full_wrapper">
            <img src={assets.ic_wrong_close} alt="wrong" />
            <section className="explaination_wrapper">
              <h4>Incorrect</h4>
              <h5>{`The correct answer is ${questionDetails[qIndex]?.ANSWER}`}</h5>
              <section className="explaination_container">
                <h5>Explanation:</h5>
                <h6><LatexRenderer text={questionDetails[qIndex]?.EXPLANATION} /></h6>
              </section>
            </section>
          </section>
        )}

        <section className="options-section">
          {q?.options?.map((option, index) => {
            const isSelected = selOpt === index;
            let optionClass = "option_quiz";
            if (isSelected) {
              if (!mode) {
                optionClass += isCorrectQ ? " option-correct" : " option-wrong";
              } else {
                optionClass += " selected";
              }
            }
            return (
              <label key={index} className={optionClass}>
                <input
                  type="radio"
                  name={`quiz-option-${qIndex}`}
                  checked={isSelected}
                  disabled={!mode && isAnsweredQ}
                  onChange={() => handleOptionSelect(index, qIndex)}
                />
                <section className="option-text">
                  <span className="option-letter">
                    {String.fromCharCode(65 + index)}.{" "}
                  </span>
                  <LatexRenderer text={option} />
                </section>
              </label>
            );
          })}
        </section>
      </section>

      <div
        className={`scroll_indicator_container ${showScrollHint ? "visible" : "hidden"}`}
        onClick={() => {
          scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollTop + 100,
            behavior: "smooth",
          });
        }}
      >
        <div className="scroll_hint">
          <span>Scroll for more</span>
          <img src={assets.ic_chevron_down} alt="scroll down" />
        </div>
      </div>

      {!isReviewMode && (
        <div className="quiz-nav-footer">
          <button
            className="prev_btn_wrapper"
            onClick={(e) => {
              e.stopPropagation();
              if (qIndex > 0) setCurrentQIndex(qIndex - 1);
            }}
            disabled={qIndex === 0}
          >
            <img src={assets.ic_left_arrow_blue} alt="prev" />
            <h5>Previous Question</h5>
          </button>
          <button
            className={
              qIndex === questions?.length - 1
                ? "end_quiz_btn_inline"
                : "nexts_btn_wrapper"
            }
            onClick={(e) => {
              e.stopPropagation();
              if (qIndex === questions?.length - 1) {
                setShowReviewPopup(true);
                setIsQuizActive(false);
              } else {
                setCurrentQIndex(qIndex + 1);
              }
            }}
          >
            <h5>
              {qIndex === questions?.length - 1 ? "End Quiz" : "Next Question"}
            </h5>
            <img src={assets.ic_arrow_white} alt="next" />
          </button>
        </div>
      )}
    </Fragment>
  );
};

export const MockTest = () => {
  const {
    user,
    selectedExam,
    selectedYear,
    setSelectedYear,
    classdetails,
    years,
    yearRef,
    openDropdown,
    setOpenDropdown,
    isStartEnabled,
    isLoading,
    questions,
    currentQIndex,
    questionDetails,
    selectedAnswers,
    markDetails,
    showMarks,
    isQuizActive,
    mode,
    hidePanals,
    submitLoading,
    timeRemaining,
    setTimeRemaining,
    setHidePanals,
    setCurrentQIndex,
    setIsQuizActive,
    getQuizDuration,
    handleOptionSelect,
    handleNext,
    handlePrevious,
    onStartQuiz,
    onSubmitQuiz,
    onRetakeQuiz,
    onCancel,
    onToggleQuizBtn,
    showSetupPopup,
    setShowSetupPopup,
    confirmStartQuiz,
    showReviewPopup,
    setShowReviewPopup,
    isReviewMode,
    setIsReviewMode,
  } = useHandler();

  const [timeNote, setTimeNote] = useState(null);
  const [shownNotes, setShownNotes] = useState(new Set());

  // ── Fullscreen Logic for Exam Mode ──
  const toggleFullScreen = (enable) => {
    try {
      if (enable) {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch((err) => {
            console.error(
              `Error attempting to enable full-screen mode: ${err.message}`,
            );
          });
        }
      } else {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => {
            console.error(
              `Error attempting to exit full-screen mode: ${err.message}`,
            );
          });
        }
      }
    } catch (error) {
      console.warn("Fullscreen API not supported or blocked");
    }
  };

  useEffect(() => {
    if (isQuizActive && mode) {
      toggleFullScreen(true);
    } else {
      toggleFullScreen(false);
    }
  }, [isQuizActive, mode]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        toggleFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    if (isQuizActive && mode) {
      document.body.classList.add("hide_sidebar");
    } else {
      document.body.classList.remove("hide_sidebar");
    }
    return () => document.body.classList.remove("hide_sidebar");
  }, [isQuizActive, mode]);

  const totalQuestions = questions?.length;

  useEffect(() => {
    if (!isQuizActive || timeRemaining <= 0) return;

    const totalTime = getQuizDuration();
    if (totalTime <= 0) return;
    const halfTime = Math.floor(totalTime / 2);

    // Halfway mark
    if (timeRemaining === halfTime && !shownNotes.has("halfway")) {
      setTimeNote({
        message:
          "You're halfway through! Keep up the great pace. You've got this! 💪",
        type: "info",
      });
      setShownNotes((prev) => new Set(prev).add("halfway"));
      setTimeout(() => setTimeNote(null), 5000);
    }

    // Low time mark (5 minutes)
    if (timeRemaining === 300 && !shownNotes.has("low_5")) {
      setTimeNote({
        message:
          "Quick heads up: Only 5 minutes remaining! Finalize your answers. ⏳",
        type: "warning",
      });
      setShownNotes((prev) => new Set(prev).add("low_5"));
      setTimeout(() => setTimeNote(null), 6000);
    }

    // Critical time (1 minute)
    if (timeRemaining === 60 && !shownNotes.has("critical_1")) {
      setTimeNote({
        message:
          "Last minute! Please submit your test now to avoid auto-submission. 🚨",
        type: "critical",
      });
      setShownNotes((prev) => new Set(prev).add("critical_1"));
      setTimeout(() => setTimeNote(null), 8000);
    }
  }, [timeRemaining, isQuizActive, getQuizDuration, shownNotes]);

  // useEffect(() => {
  //   if (questions.length > 0 && isQuizActive) {
  //     setTimeRemaining(getQuizDuration());
  //   }
  // }, [questions.length, isQuizActive]);

  useEffect(() => {
    if (!isQuizActive || questions.length === 0) return;
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsQuizActive(false);
          onSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isQuizActive, questions.length]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const correctCount = markDetails?.DETAILS?.filter(
    ({ IS_CORRECT }) => IS_CORRECT === true,
  ).length;
  const wrongCount = markDetails?.DETAILS?.filter(
    ({ USER_ANSWER, IS_CORRECT }) => USER_ANSWER && IS_CORRECT === false,
  ).length;
  const notAnsweredCount = markDetails?.NOT_ATTEMPTED_COUNT;
  const scorePercentage = Math.round(
    (markDetails?.TOTAL_MARKS / markDetails?.MAX_MARKS) * 100,
  );

  const renderScoreCardDetails = (item, index) => {
    const {
      QUESTION,
      IS_CORRECT,
      USER_ANSWER,
      CORRECT_ANSWER,
      REMARK,
      NOT_ATTEMPTED,
    } = item || {};
    return (
      <Fragment key={index}>
        {IS_CORRECT === true ? (
          <section className="questions_container1">
            <img src={assets.ic_correct_tick} alt="correct_tick" />
            <section className="questions_content">
              <h4>{`Question ${index + 1}`}</h4>
              <h5>
                <LatexRenderer text={QUESTION} /> <span><LatexRenderer text={CORRECT_ANSWER} /></span>
              </h5>
              {REMARK && <h5>Remarks: <LatexRenderer text={REMARK} /></h5>}
            </section>
          </section>
        ) : (
          <section
            className={`${
              NOT_ATTEMPTED
                ? "questions_container_not_attempted"
                : "questions_container3"
            }`}
          >
            {NOT_ATTEMPTED ? (
              <img src={assets.ic_unattempt} alt="wrong" />
            ) : (
              <img src={assets.ic_wrong_close} alt="wrong" />
            )}
            <section className="questions_content">
              <h4>{`Question ${index + 1}`}</h4>
              <h5><LatexRenderer text={QUESTION} /></h5>
              <section className="important_info_wrapper">
                <img src={assets.ic_info} alt="info" />
                <section className="important_details">
                  <h5 className="your_ans">Your Answer: <LatexRenderer text={USER_ANSWER || "Not Answered"} /></h5>
                  <h5 className="correct_ans">Correct Answer: <LatexRenderer text={CORRECT_ANSWER} /></h5>
                  {REMARK && <h5>Remarks: <LatexRenderer text={REMARK} /></h5>}
                </section>
              </section>
            </section>
          </section>
        )}
      </Fragment>
    );
  };

  return (
    <>
      {timeNote && (
        <div className={`time_note_notification ${timeNote.type}`}>
          <div className="note_icon">
            <img src={assets.ic_info} alt="note" />
          </div>
          <p>{timeNote.message}</p>
        </div>
      )}
      <section className="chat_layout">
        {!hidePanals && (
          <section className="nav_context_contanier">
            <section className="nav_learn_title">
              <h4>Learning Context</h4>
              <h6>Set your topic to start learning</h6>
            </section>

            <section className="nav_subject_details">
              <section className="board_wrapper">
                <section className="class">
                  <h5>Competitive Exam</h5>
                  <h4>{classdetails?.data?.focus || "Loading..."}</h4>
                </section>
              </section>
            </section>

            <section className="quiz_nav_mode_wrapper">
              <section className="quiz_nav_mode_container">
                <h4>Instant Mode</h4>
                <ToggleButton isToggle={mode} onToggle={onToggleQuizBtn} />
                <h4>Exam Mode</h4>
              </section>
            </section>

            <section className="nav_subject_wrapper">
              <QuizDropdown
                label="Year"
                refKey={yearRef}
                dropKey="year"
                value={selectedYear}
                options={years}
                onSelect={setSelectedYear}
                placeholder="Select Year"
                prerequisite={!!selectedExam}
                prerequisiteLabel="Exam"
                setOpenDropdown={setOpenDropdown}
                openDropdown={openDropdown}
                isLoading={isLoading}
              />

              <button
                className="start_quiz_wrapper"
                disabled={!isStartEnabled}
                onClick={onStartQuiz}
              >
                Evaluate Me
              </button>
            </section>
          </section>
        )}

        {showSetupPopup && (
          <QuizSetupModal
            classdetails={classdetails}
            selectedYear={selectedYear}
            getQuizDuration={getQuizDuration}
            setShowSetupPopup={setShowSetupPopup}
            setHidePanals={setHidePanals}
            setIsQuizActive={setIsQuizActive}
            confirmStartQuiz={confirmStartQuiz}
            mode={mode}
          />
        )}

        <section className="chat-panal">
          {questions.length > 0 && !showMarks && (
            <div className="quiz-header">
              <div className="brand">
                <div className="brand-icon">
                  {user?.data?.name?.slice(0, 1)}
                </div>
                <span className="brand-text">{user?.data?.name}</span>
              </div>
              <div className="header-meta">
                <div className="timer-pill">
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{formatTime(timeRemaining)}</span>
                </div>
                <span className="q-counter">
                  {`Question ${currentQIndex + 1} of ${totalQuestions}`}
                </span>
                <button
                  className="nav_submit_btn"
                  onClick={() => {
                    // setIsQuizActive(false);
                    setShowReviewPopup(true);
                  }}
                >
                  {submitLoading && <div className="spinner"></div>}
                  Finish Attempt
                </button>
              </div>
            </div>
          )}

          <section className="chat_body">
            <section className="quiz-main-container">
              {showReviewPopup && (
                <QuestionPaletteScreen
                  questions={questions}
                  selectedAnswers={selectedAnswers}
                  setCurrentQIndex={setCurrentQIndex}
                  setIsReviewMode={setIsReviewMode}
                  setIsQuizActive={setIsQuizActive}
                  setShowReviewPopup={setShowReviewPopup}
                  onSubmitQuiz={onSubmitQuiz}
                  submitLoading={submitLoading}
                />
              )}

              {!showReviewPopup &&
                !isQuizActive &&
                !showMarks &&
                !questionDetails.length && <MockTestHero />}

              {!showReviewPopup &&
                isReviewMode &&
                questionDetails.length > 0 && (
                  <section className="review_mode_layout">
                    <section className="review_question_area">
                      <div className="premium_quiz_card">
                        <QuestionCardInner
                          qIndex={currentQIndex}
                          questions={questions}
                          selectedAnswers={selectedAnswers}
                          mode={mode}
                          questionDetails={questionDetails}
                          handleOptionSelect={handleOptionSelect}
                          isReviewMode={isReviewMode}
                          setCurrentQIndex={setCurrentQIndex}
                          setShowReviewPopup={setShowReviewPopup}
                          setIsQuizActive={setIsQuizActive}
                        />
                      </div>
                    </section>
                    <section className="review_sidebar">
                      <div className="review_sidebar_header">
                        <span>Question Palette</span>
                        <span className="review_q_badge">
                          {questions.length} Questions
                        </span>
                      </div>
                      <div className="review_sidebar_grid">
                        {questions.map((_, index) => (
                          <div
                            key={index}
                            className={`review_sidebar_circle ${
                              index === currentQIndex
                                ? "rs_current"
                                : selectedAnswers[index] !== undefined
                                  ? "rs_answered"
                                  : "rs_unanswered"
                            }`}
                            onClick={() => {
                              setCurrentQIndex(index);
                              setIsQuizActive(true);
                            }}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </div>
                        ))}
                      </div>
                      <button
                        className="review_sidebar_submit"
                        onClick={() => {
                          setIsReviewMode(false);
                          setIsQuizActive(false);
                          onSubmitQuiz();
                        }}
                      >
                        {submitLoading && (
                          <section className="submit_test_spinner"></section>
                        )}
                        Submit Quiz
                      </button>
                    </section>
                  </section>
                )}

              {!showReviewPopup &&
                isQuizActive &&
                !isReviewMode &&
                questionDetails.length > 0 && (
                  <div className="quiz_content_main">
                    <QuizHSlider
                      questions={questions}
                      currentQIndex={currentQIndex}
                      setCurrentQIndex={setCurrentQIndex}
                      renderCard={(idx) => (
                        <QuestionCardInner
                          qIndex={idx}
                          questions={questions}
                          selectedAnswers={selectedAnswers}
                          mode={mode}
                          questionDetails={questionDetails}
                          handleOptionSelect={handleOptionSelect}
                          isReviewMode={isReviewMode}
                          setCurrentQIndex={setCurrentQIndex}
                          setShowReviewPopup={setShowReviewPopup}
                          setIsQuizActive={setIsQuizActive}
                        />
                      )}
                    />
                  </div>
                )}

              {showMarks && !markDetails?.DETAILS?.length && (
                <section className="submit_loading_screen">
                  <div className="submit_spinner"></div>
                  <p>Evaluating your performance...</p>
                </section>
              )}

              {showMarks && markDetails?.DETAILS?.length > 0 && (
                <section className="award_container">
                  <div className="award_header">
                    <img
                      src={assets.ic_award}
                      alt="award"
                      className="award_img"
                    />
                    <p className="compelete_text">
                      You've successfully completed the Mock Test
                    </p>
                    <div className="syllabus_badge">
                      <span>{selectedExam}</span> • <span>{selectedYear}</span>
                    </div>
                  </div>

                  <div className="score_grid">
                    <div className="score_card primary">
                      <img src={assets.ic_score} alt="score" />
                      <div className="score_val">{scorePercentage}%</div>
                      <div className="score_label">Overall Score</div>
                    </div>
                    <div className="score_card success">
                      <img src={assets.ic_correct} alt="correct" />
                      <div className="score_val">{correctCount}</div>
                      <div className="score_label">Correct</div>
                    </div>
                    <div className="score_card danger">
                      <img src={assets.ic_wrong} alt="wrong" />
                      <div className="score_val">{wrongCount}</div>
                      <div className="score_label">Incorrect</div>
                    </div>
                    <div className="score_card gray">
                      <img src={assets.ic_unattempt} alt="unattempt" />
                      <div className="score_val">{notAnsweredCount}</div>
                      <div className="score_label">Skipped</div>
                    </div>
                  </div>

                  <div className="review_section">
                    <h4>Detailed Review</h4>
                    <div className="review_list">
                      {markDetails?.DETAILS.map(renderScoreCardDetails)}
                    </div>
                  </div>

                  <div className="result_actions">
                    <button className="retake_btn" onClick={onRetakeQuiz}>
                      <img src={assets.ic_retake} alt="retake" />
                      Retake Mock Test
                    </button>
                    <button className="back_btn" onClick={onCancel}>
                      <img src={assets.ic_left_arrow_blue} alt="back" />
                      Back to Home
                    </button>
                  </div>
                </section>
              )}
            </section>
          </section>
        </section>
      </section>
    </>
  );
};

const QuizHSlider = ({
  questions,
  renderCard,
  currentQIndex,
  setCurrentQIndex,
}) => {
  const trackRef = useRef(null);
  const rafIdRef = useRef(null); // cancel in-flight rAF
  const cooldownRef = useRef(false); // brief lock between card transitions
  const wheelAccRef = useRef(0); // accumulated deltaX within one gesture
  const wheelTimerRef = useRef(null); // debounce to reset accumulator on silence
  const [isReady, setIsReady] = useState(false);

  // easeOutCubic — instant start, smooth deceleration (iOS-like feel)
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  // Interruptible rAF animator — if called mid-animation, starts from current position
  const animateTo = useCallback((track, targetLeft) => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    const start = track.scrollLeft;
    const dist = targetLeft - start;
    if (Math.abs(dist) < 1) {
      cooldownRef.current = false;
      return;
    }
    const duration = 360;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      track.scrollLeft = start + dist * ease(p);
      if (p < 1) {
        rafIdRef.current = requestAnimationFrame(step);
      } else {
        track.scrollLeft = targetLeft;
        rafIdRef.current = null;
        // release cooldown slightly after settle so fast swipes feel responsive
        setTimeout(() => {
          cooldownRef.current = false;
        }, 80);
      }
    };
    rafIdRef.current = requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Center a card index in the track
  const goToIndex = useCallback(
    (idx, track) => {
      const t = track || trackRef.current;
      if (!t) return;
      const card = t.children[idx];
      if (!card) return;
      const target =
        t.clientWidth > 0
          ? card.offsetLeft - (t.clientWidth - card.offsetWidth) / 2
          : idx === 0
            ? 0
            : card.offsetLeft;
      animateTo(t, Math.max(0, target));
    },
    [animateTo],
  );

  // External index change (palette click, dots)
  useEffect(() => {
    setIsReady(false);
    cooldownRef.current = true;
    goToIndex(currentQIndex);
    const timer = setTimeout(() => {
      setIsReady(true);
      cooldownRef.current = false;
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length]);

  useEffect(() => {
    if (!isReady) return;
    cooldownRef.current = true;
    goToIndex(currentQIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQIndex, isReady]);

  // IntersectionObserver — keep dots/counter in sync while CSS snap or touch settles
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!isReady || cooldownRef.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.85) {
            const idx = Array.from(track.children).indexOf(entry.target);
            if (idx >= 0) setCurrentQIndex(idx);
          }
        });
      },
      { root: track, threshold: 0.85 },
    );
    Array.from(track.children).forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, [questions.length, setCurrentQIndex, isReady]);

  // Wheel — vertical (deltaY) scrolls card content naturally.
  // Only horizontal trackpad swipe (deltaX) navigates between cards.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const canScrollVertical = (el, deltaY) => {
      while (el && el !== track) {
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY;
        if (overflowY === "auto" || overflowY === "scroll") {
          if (deltaY > 0 && el.scrollTop + el.clientHeight < el.scrollHeight) {
            return true;
          }
          if (deltaY < 0 && el.scrollTop > 0) {
            return true;
          }
        }
        el = el.parentElement;
      }
      return false;
    };
    const onWheel = (e) => {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      // If the inner card content can scroll vertically, do not hijack the wheel.
      if (canScrollVertical(e.target, e.deltaY)) return;
      // Only intercept clear horizontal gestures.
      if (absX < 50 || absX <= absY * 2) return;
      e.preventDefault();
      // Debounce: reset accumulator 200ms after gesture ends
      clearTimeout(wheelTimerRef.current);
      wheelTimerRef.current = setTimeout(() => {
        wheelAccRef.current = 0;
      }, 200);
      // While animating, swallow all events without accumulating
      if (cooldownRef.current) return;
      wheelAccRef.current += e.deltaX;
      // Require 60px of intentional scroll before advancing one card
      if (Math.abs(wheelAccRef.current) < 60) return;
      const direction = wheelAccRef.current > 0 ? 1 : -1;
      wheelAccRef.current = 0;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      setCurrentQIndex((prev) => {
        const next =
          direction > 0 ? Math.min(prev + 1, maxIdx) : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    track.addEventListener("wheel", onWheel, { passive: false });
    return () => track.removeEventListener("wheel", onWheel);
  }, [questions.length, goToIndex, setCurrentQIndex]);

  // Arrow keys — Left/Right to navigate cards
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      const track = trackRef.current;
      setCurrentQIndex((prev) => {
        const next =
          e.key === "ArrowRight"
            ? Math.min(prev + 1, maxIdx)
            : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [questions.length, goToIndex, setCurrentQIndex]);

  // Touch swipe — horizontal swipe of 30px+ advances one card
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let startX = 0,
      startY = 0;
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dx = startX - e.changedTouches[0].clientX;
      const dy = startY - e.changedTouches[0].clientY;
      if (Math.abs(dx) < Math.abs(dy) || Math.abs(dx) < 30) return;
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      setCurrentQIndex((prev) => {
        const next =
          dx > 0 ? Math.min(prev + 1, maxIdx) : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    track.addEventListener("touchstart", onTouchStart, { passive: true });
    track.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      track.removeEventListener("touchstart", onTouchStart);
      track.removeEventListener("touchend", onTouchEnd);
    };
  }, [questions.length, goToIndex, setCurrentQIndex]);

  return (
    <section className="quiz_hslider_wrapper">
      <div className="quiz_hslider_track" ref={trackRef}>
        {questions.map((_, qIndex) => (
          <div
            className={`quiz_hslider_card${qIndex === currentQIndex ? " is_active" : ""}`}
            key={qIndex}
          >
            <div className="quiz_hslider_card_inner">{renderCard(qIndex)}</div>
          </div>
        ))}
      </div>
    </section>
  );
};
